import './file-upload.scss';

import { first } from 'lodash';
import {
  ChangeEvent,
  FocusEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import {
  FileTypeImage,
  FileTypePdf,
  formatFileSize,
  formatMimeType,
  getBase64FromServer,
  getUploadFileDataFromFile,
  isValidFileSize,
  isValidFileType,
  UploadFileItemData,
} from '../../data/utils/upload.utils';
import { Button, ButtonIcon } from '../button/button';
import { CustomFormattedMessage } from '../intl/custom-formatted-message';

export type FileUploadProps = {
  error?: string | { error: string; [key: string]: string };
  fileTypes: string[];
  fileSize: string;
  value?: UploadFileItemData | null;
  onChange?: (
    value: UploadFileItemData | null,
    e: ChangeEvent<HTMLElement>
  ) => void;
  onBlur?: (
    value: UploadFileItemData | null,
    e: FocusEvent<HTMLElement>
  ) => void;
};

const isImage = (file: string) =>
  isValidFileType(formatMimeType(file), FileTypeImage);

export function FileUpload({
  fileTypes = [],
  fileSize = '25MB',
  onChange,
  onBlur,
  ...props
}: FileUploadProps) {
  const intl = useIntl();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState<UploadFileItemData | null>(null);
  const [error, setError] = useState<
    string | { error: string; [key: string]: string } | null
  >(null);

  useEffect(() => {
    setValue(props.value ?? null);
  }, [props.value]);

  const getFile = useCallback(
    async (file: File | null | undefined) => {
      let data: UploadFileItemData | null = null;

      if (file) {
        if (!isValidFileType(file?.name, fileTypes)) {
          setError(
            intl.formatMessage({
              id: 'common.file-upload.error.type',
            })
          );
        } else if (!isValidFileSize(file?.size, fileSize)) {
          setError(
            intl.formatMessage({
              id: 'common.file-upload.error.size',
            })
          );
        } else {
          setError(null);
          data = await getUploadFileDataFromFile(file);
        }
      }

      return data;
    },
    [fileTypes, fileSize, intl]
  );

  const onHandleFiles = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const fileData = await getFile(
        first((e.currentTarget as HTMLInputElement).files)
      );

      setValue(fileData);

      onChange?.(fileData, e as unknown as ChangeEvent<HTMLElement>);

      onBlur?.(fileData, e as unknown as FocusEvent<HTMLElement>);
    },
    [getFile, onChange, onBlur]
  );

  const onHandleButton = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      setValue(null);

      onChange?.(null, e as unknown as ChangeEvent<HTMLElement>);

      onBlur?.(null, e as unknown as FocusEvent<HTMLElement>);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [fileInputRef, onChange, onBlur]
  );

  const fileType = useMemo(() => {
    if (isValidFileType(formatMimeType(value?.originalFileName), FileTypeImage))
      return 'image';
    if (isValidFileType(formatMimeType(value?.originalFileName), FileTypePdf))
      return 'pdf';
    return 'none';
  }, [value?.originalFileName]);

  const getImagePreview = useCallback(async (v: UploadFileItemData | null) => {
    if (!v) return '';
    if (!v.originalFileName) return '';
    if (isImage(v?.originalFileName)) {
      if (v.base64) {
        return v.base64;
      }
      if (v.thumbNail) {
        const base64 = getBase64FromServer(v?.thumbNail);
        return base64 ?? '';
      }
    }
    return '';
  }, []);

  const [imageUrl, setImageUrl] = useState<string>('');

  useEffect(() => {
    const loadImage = async () => {
      if (value) {
        const url = await getImagePreview(value);
        setImageUrl(url);
      }
    };
    loadImage();
  }, [value, getImagePreview]);

  return (
    <div
      className={`file-upload${error ? ' invalid' : ''}${
        !value ? ' empty' : ''
      }`}
    >
      <div className="file-upload__main">
        {value ? (
          <>
            <div className="file-upload__main__preview">
              <div className="thumbnail">
                {value && fileType === 'image' && (
                  <img src={imageUrl} alt={value.originalFileName} />
                )}
                {value && fileType === 'pdf' && <i className="icon pdf" />}
              </div>
            </div>
            <div className="file-upload__main__content">
              <h2>
                <CustomFormattedMessage id="common.file-upload.file-name" />
              </h2>
              <p>{value?.fileName ?? 'select file'}</p>
            </div>
            <div className="file-upload__main__size">
              <h2>
                <CustomFormattedMessage id="common.file-upload.file-size" />
              </h2>
              {value?.size && <p>{formatFileSize(value?.size ?? 0, 'MB')}</p>}
            </div>
            <input
              type="file"
              accept={fileTypes.join(',')}
              onChange={onHandleFiles}
              ref={fileInputRef}
            />

            <div className="file-upload__main__actions">
              <Button onClick={onHandleButton} icon={ButtonIcon.DELETE} />
            </div>
          </>
        ) : (
          <div className="file-upload__main__placeholder">
            <i className="icon upload" />
            <h2>
              <CustomFormattedMessage id="common.file-upload.placeholder" />
            </h2>
            <p>
              <CustomFormattedMessage
                id="common.file-upload.max-file-size"
                values={{ size: fileSize }}
              />
            </p>
            <input
              type="file"
              accept={fileTypes.join(',')}
              onChange={onHandleFiles}
              ref={fileInputRef}
            />
          </div>
        )}
      </div>
      {error && (
        <div className="file-upload__error">
          {typeof error === 'string' ? error : error.error}
        </div>
      )}

      {/* <button type="button" onClick={onHandleButton} /> */}
    </div>
  );
}
